
  import { api } from "@/services/Api";
  import {
    defineComponent,
    ref,
    onMounted,
    onUnmounted,
    computed,
  } from "vue";
  import swal from "sweetalert2";
  import { useI18n } from "vue-i18n";
  import InputText from "primevue/inputtext";
  import { useStore } from "@/store";
  import { useRouter, useRoute } from "vue-router";
  import TextInput from "@/components/Inputs/TextInput.vue";
  import { OrderSearchType, OrderStatus } from "@/models/Enums";
  import q from "q";
  import { ShiftListFilter, ShiftListResponseModel } from "@/models/Shift";
  import DateInput from "@/components/Inputs/DateInput.vue";
  import { printPdf } from "@/utils/printer";
  import Swal from "sweetalert2";
  import { Settings } from "@/models/Interfaces";
  import useOrder from '@/modules/useOrder';
  
  export default defineComponent({
    name: "ReservedOrderSearch",
    components: { InputText, TextInput, DateInput },
    beforeRouteEnter(to, from, next) {
      if (from.name == "home") {
        to.params.fromHome = "1";
      }
      next();
    },
    async setup() {
      const { awaitTask } = useOrder();
      const route = useRoute();
      const searchBy = ref(OrderSearchType.OrderNumber);
      const loaded = ref(false);
      const store = useStore();
      const shiftFilter = ref<ShiftListFilter>(new ShiftListFilter());
      shiftFilter.value = store.state.shiftFilter;
      const searchWas = ref(true);
      const busy = ref(false);
      const hasMore = ref(true);
      const showNoResult = ref(false);
      const router = useRouter();
      const { t } = useI18n();
      const cashierId = ref(store.state.user?.cashierGuid);
      const filterExist = computed(() => {
        if (shiftFilter.value?.startDate) {
          return true;
        }
        if (shiftFilter.value?.endDate) {
          return true;
        }
        return false;
      });
      const shiftList = ref<ShiftListResponseModel[]>([]);
      const settings = ref(store.getters.printerSettings as Settings);
      function createDebounce() {
        let timeout = 0;
        return function (fnc: any, delayMs?: number) {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            fnc();
          }, delayMs || 1200);
        };
      }
      async function loadMore() {
        await search();
      }
  
      async function onChangeKeyword() {
        if (shiftFilter.value.keywords.length < 2) {
          return;
        }
        await onClickSearch();
      }
      async function onClickSearch() {
        var valid = validateFilter();
        if (!valid) {
          return;
        }
        shiftList.value = [];
        shiftFilter.value.pageIndex = 0;
        hasMore.value = true;
        await search();
        if (shiftList.value.length == 0) {
          showNoResult.value = true;
        } else {
          showNoResult.value = false;
          searchWas.value = true;
        }
      }
      async function onClickSearchBy() {
        var valid = validateFilter();
        if (!valid) {
          return;
        }
        shiftList.value = [];
        shiftFilter.value.pageIndex = 0;
        hasMore.value = true;
        await searchShiftList();
        if (shiftList.value.length == 0) {
          showNoResult.value = true;
        } else {
          showNoResult.value = false;
          searchWas.value = true;
        }
      }
      function validateFilter() {
        if (shiftFilter.value.keywords && shiftFilter.value.keywords.length < 2) {
          swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: t("cashier.search.keywords-length"),
          });
          return false;
        }
        return true;
      }
      async function search() {
        if (busy.value) {
          return;
        }
        var valid = validateFilter();
        if (!valid) {
          return;
        }
        swal.showLoading();
        busy.value = true;
        var apiResultPromise = api.searchShiftList(shiftFilter.value);
        await q.delay(400);
        var apiResult = await apiResultPromise;
        busy.value = false;
        if (apiResult.errorMessage) {
          swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: apiResult.errorMessage,
          });
          return;
        }
        if (!apiResult.data) {
          swal.close();
          return;
        }
        shiftFilter.value.pageIndex++;
        if (apiResult.data.length < 50) {
          hasMore.value = false;
        }
        if (shiftFilter.value.pageIndex > 0) {
            shiftList.value = [...shiftList.value, ...apiResult.data];
        } else {
            shiftList.value = apiResult.data;
        }
        swal.close();
      }
      async function searchShiftList() {
        var valid = validateFilter();
        if (!valid) {
          return;
        }
        swal.showLoading();
        busy.value = true;
        var apiResultPromise = api.searchShiftList(shiftFilter.value);
        await q.delay(400);
        var apiResult = await apiResultPromise;
        busy.value = false;
        if (apiResult.errorMessage) {
          swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: apiResult.errorMessage,
          });
          return;
        }
        if (!apiResult.data) {
          swal.close();
          return;
        }
        shiftFilter.value.pageIndex++;
        if (apiResult.data.length < 50) {
          hasMore.value = false;
        }
        if (shiftFilter.value.pageIndex > 0) {
          shiftList.value = [...shiftList.value, ...apiResult.data];
        } else {
            shiftList.value = apiResult.data;
        }
        swal.close();
      }
      async function handleScroll() {
        if (!hasMore.value) {
          return;
        }
        const shifts = document.getElementById("list-orders");
        if (!shifts) {
          return;
        }
        if (shifts.scrollTop + shifts.clientHeight >= shifts.scrollHeight - 5) {
          await loadMore();
        }
      }
      function clearFilter() {
        shiftFilter.value = new ShiftListFilter();
      }
      function back() {
        if(shiftFilter.value.advancedSearch){
            shiftFilter.value.advancedSearch = false;
            searchWas.value = true;
            return;
        }

        router.push({ name: "home" });
      }
      function showAll() {
        clearFilter();
        shiftList.value = [];
        search();
      }
      function advancedSearch() {
        searchWas.value = !searchWas.value;
        shiftFilter.value.advancedSearch = true;
      }

      const onMountedCall = async () => {
        if (route.params.fromHome) {
          shiftFilter.value = new ShiftListFilter();
        } else {
          searchWas.value = true;
          showNoResult.value = false;
        }
        loaded.value = true;
        const ordersList = document.getElementById("list-orders");
        if (ordersList) {
          ordersList.addEventListener("scroll", handleScroll);
        }
        search();
      };
      onMounted(onMountedCall);
  
      const onUnmountedCall = async () => {
        const app = document.getElementById("main-content");
        if (app) {
          app.removeEventListener("scroll", handleScroll);
        }
      };
      onUnmounted(onUnmountedCall);

      async function printShiftReport(shiftId: string, isOpenShiftReport: boolean){
        if(shiftId){
          const shiftReportResponse = await api.generateShiftReport(settings.value.shiftPrinterSize, shiftId, isOpenShiftReport);

          if(shiftReportResponse.error){
              Swal.fire({
                  icon: "error",
                  text: shiftReportResponse.errorMessage,
              });
              return;
          }

          const taskId = shiftReportResponse.data;

          awaitTask(
              () => api.waitExportTask(taskId!),
                  async (url) => {
                      await printPdf(url);
                      Swal.close();
                  }
              );
        }
      }

      return {
        loaded,
        shiftList,
        debounce: createDebounce(),
        OrderStatus,
        searchWas,
        shiftFilter,
        onClickSearch,
        showNoResult,
        onChangeKeyword,
        back,
        search,
        showAll,
        advancedSearch,
        filterExist,
        searchBy,
        onClickSearchBy,
        cashierId,
        printShiftReport
      };
    },
  });
  